import { Method } from "axios";
import { AxiosAuthRefreshRequestConfig } from "axios-auth-refresh";
import { getInternalApiClient } from "./InternalApiClient";
import { getExternalApiClient } from "./ExternalApiClient";
import { HTTP_METHODS } from "../constants/DataProvider.constants";
import { IDataProvider } from "../IDataProvider";

export const customRequest = <T>({
  method = HTTP_METHODS.GET,
  endpoint,
  baseURL,
  payload = {},
  headers,
  isExternal,
  skipAccessToken,
  withCredentials,
}: {
  endpoint: string;
  method?: Method;
  baseURL?: string;
  payload?: Record<string, any>;
  isExternal?: boolean;
  skipAccessToken?: boolean; // Optional flag to skip token refreshing for certain requests, useful for internal public API requests that don't require authentication
  headers?: { [headerName: string]: string };
  withCredentials?: boolean;
}): Promise<IDataProvider.ICustomRequestResponse<T>> => {
  const api = isExternal ? getExternalApiClient() : getInternalApiClient();
  const requestConfig: AxiosAuthRefreshRequestConfig = {
    method,
    url: endpoint,
    baseURL,
    headers,
    skipAuthRefresh: skipAccessToken,
    withCredentials,
  };
  if (method === HTTP_METHODS.GET) {
    requestConfig.params = payload;
  } else {
    requestConfig.data = payload;
  }

  return api.request(requestConfig);
};

/**
 * Add query params to a url
 * @param obj: object of queries to be added in url
 * @param prefix: prefix key to added before every query key
 * @returns string of query param
 */
export const queryParamsStringify = (
  obj: Record<string, any>,
  prefix = "",
): string => {
  return Object.keys(obj)
    .map((key) => {
      const value = obj[key];
      const prefixedKey = prefix
        ? `${prefix}[${encodeURIComponent(key)}]`
        : encodeURIComponent(key);

      if (value === null || value === undefined) {
        return "";
      }

      if (typeof value === "object" && !Array.isArray(value)) {
        return queryParamsStringify(value, prefixedKey); // Recursively handle nested objects.
      }

      if (Array.isArray(value)) {
        return value
          .map((v) => `${prefixedKey}[]=${encodeURIComponent(v)}`)
          .join("&"); // Handle arrays.
      }

      return `${prefixedKey}=${encodeURIComponent(value)}`;
    })
    .filter((pair) => pair.length > 0) // Remove empty strings for null/undefined.
    .join("&");
};
